import { Button, Checkbox, Loader, Modal, TextInput } from '@mantine/core';
import { joinNames, MAIL_TEMPLATES, type ManualTemplateKey } from '@reditelji/shared';
import { useEffect, useRef, useState } from 'react';
import { useMailMutations, useMailPreview, useMailReview } from '../api/mail';
import { BodyEditor } from './TemplateEditor';

/**
 * The last step before a mail leaves: the coordinator sees it rendered for this
 * one person, with their real name, shifts and link — or, when others read the
 * same inbox (skupni predal), for everyone ticked: one mail, a button each.
 * The household is offered with those owed the mail ticked from the start;
 * nobody is included silently.
 *
 * The body can be tweaked for this send alone. That edit never touches the
 * stored template — the wording everyone else gets stays as it was — so a
 * one-off sentence for one volunteer does not quietly become the standard.
 */
export function SendMailDialog({
  volunteerId,
  volunteerName,
  templateKey,
  onClose,
}: {
  volunteerId: string;
  volunteerName: string;
  templateKey: ManualTemplateKey;
  onClose: () => void;
}) {
  // Null until the review says who is owed the mail; then the ticked set.
  const [withIds, setWithIds] = useState<string[] | null>(null);
  const review = useMailReview(volunteerId, templateKey, withIds ?? []);
  const { sendOne } = useMailMutations();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const data = review.data;
  // Seeded once, not on every `data` identity.
  //
  // The query is configured to refetch freely, and a refetch hands back a new
  // object even when the content is unchanged — re-seeding on it would wipe a
  // coordinator's one-off edit mid-sentence and then mail the stored template
  // while they believed their words went out.
  const seeded = useRef(false);
  useEffect(() => {
    if (!data || seeded.current) return;
    seeded.current = true;
    setSubject(data.template.subject);
    setBody(data.template.body);
    setWithIds(data.household.filter((h) => h.due).map((h) => h.id));
  }, [data]);

  const def = MAIL_TEMPLATES[templateKey];
  const blocked = data?.blocked ?? null;
  const ticked = withIds ?? [];
  const toggle = (id: string, on: boolean) =>
    setWithIds(on ? [...ticked, id] : ticked.filter((x) => x !== id));

  /*
   * Whether this send carries an edit, which is not the same as whether the
   * editor is open. Keying the override on the open editor meant that going
   * back to the preview to check the wording silently threw the edit away and
   * mailed the stored template instead.
   */
  const edited =
    data !== undefined && (subject !== data.template.subject || body !== data.template.body);
  const override = edited ? { subject, body } : undefined;

  // The preview has to show what will actually be sent, so an edited draft is
  // rendered by the same endpoint rather than falling back to the stored one.
  const preview = useMailPreview();
  const { mutate: renderPreview } = preview;
  const [edit, setEdit] = useState<{ subject: string; html: string } | null>(null);
  const withKey = ticked.join(',');
  useEffect(() => {
    if (!edited || editing) return;
    renderPreview(
      { key: templateKey, lang: data.lang, subject, body, volunteerId, with: withKey ? withKey.split(',') : [] },
      { onSuccess: (out) => setEdit({ subject: out.subject, html: out.html }) },
    );
  }, [renderPreview, edited, editing, templateKey, subject, body, volunteerId, data, withKey]);

  const shown = edited && edit ? edit : data ? { subject: data.subject, html: data.html } : null;

  const send = () => {
    sendOne.mutate({ volunteerId, key: templateKey, override, with: ticked }, { onSuccess: onClose });
  };

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={`${def.label} — ${volunteerName}`}
      centered
    >
      {review.isPending && !data ? (
        <div className="ml-dialog-empty">
          <Loader size="sm" />
        </div>
      ) : !data ? (
        <div className="ml-dialog-empty">Predogleda ni bilo mogoče sestaviti.</div>
      ) : (
        <>
          <div className="ml-dialog-meta">
            <span className="ml-dialog-to">{data.to ?? 'brez e-naslova'}</span>
            <span className="ml-dialog-lang">{data.lang === 'en' ? 'angleško' : 'slovensko'}</span>
          </div>

          {data.household.length > 0 ? (
            <div className="ml-household">
              <div className="ml-eyebrow">SKUPNI PREDAL</div>
              <div className="ml-hint">
                Ta naslov berejo tudi drugi. Odkljukani dobijo isto sporočilo — eno pismo, vsak s
                svojo povezavo in gumbom.
              </div>
              <div className="ml-household-list">
                {data.household.map((h) => (
                  <Checkbox
                    key={h.id}
                    size="xs"
                    label={
                      h.blocked ? `${h.name} — ${h.blocked}` : h.due ? h.name : `${h.name} — že prejeto`
                    }
                    disabled={h.blocked !== null}
                    checked={ticked.includes(h.id)}
                    onChange={(e) => toggle(h.id, e.currentTarget.checked)}
                  />
                ))}
              </div>
              {data.recipients.length > 1 ? (
                <div className="ml-hint">
                  Prejemniki: {joinNames(data.recipients.map((r) => r.name), 'sl')}
                </div>
              ) : null}
            </div>
          ) : null}

          {blocked ? <div className="ml-error">Ni mogoče poslati: {blocked}.</div> : null}
          {data.unresolved.length > 0 ? (
            <div className="ml-warn">
              Neznane oznake ostanejo v besedilu: {data.unresolved.join(', ')}.
            </div>
          ) : null}

          {editing ? (
            <>
              <TextInput
                size="sm"
                label="Zadeva"
                value={subject}
                onChange={(e) => setSubject(e.currentTarget.value)}
              />
              <div className="ml-dialog-editor">
                <BodyEditor value={body} onChange={setBody} templateKey={templateKey} />
              </div>
              <div className="ml-hint" style={{ marginTop: 6 }}>
                Ta sprememba velja samo za to sporočilo. Predloga ostane, kakršna je.
              </div>
            </>
          ) : (
            <>
              <div className="ml-preview-subject">
                <span className="ml-preview-label">Zadeva</span>
                <span className="ml-preview-value">{shown?.subject ?? data.subject}</span>
              </div>
              {edited ? (
                <div className="ml-hint" style={{ margin: '6px 0' }}>
                  Predogled kaže tvojo enkratno spremembo, ne shranjene predloge.
                </div>
              ) : null}
              <iframe
                className="ml-dialog-frame"
                title="Predogled sporočila"
                sandbox="allow-same-origin"
                srcDoc={`<!doctype html><meta charset="utf-8"><style>body{margin:0}</style>${shown?.html ?? data.html}`}
              />
            </>
          )}

          <div className="ml-dialog-actions">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setEditing((v) => !v)}
              disabled={blocked !== null}
            >
              {editing ? 'Nazaj na predogled' : 'Uredi samo za to sporočilo'}
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="default" size="sm" onClick={onClose}>
              Prekliči
            </Button>
            <Button
              size="sm"
              loading={sendOne.isPending}
              disabled={blocked !== null || review.isFetching}
              onClick={send}
            >
              Pošlji
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
